import type { ReactNode, Ref } from 'react'
import { Picture, PITCH_H, PITCH_V } from '~/art'
import { cx } from './cx'
import { toneFill, type Tone } from './tone'

type PitchProps = {
  orientation?: 'horizontal' | 'vertical'
  /** Caption for screen readers, e.g. "Startelf im 4-2-3-1". Players inside stay reachable (buttons, names). */
  label: string
  /** PitchSpot / PitchPlayer elements, positioned in percent of this pitch box. */
  children?: ReactNode
  /** Sizing only, e.g. "max-w-md". */
  className?: string
  /** Portrait only: the opponent's half, goal at the top (tactics boards). */
  half?: boolean
  ref?: Ref<HTMLElement>
}

/** Markings in metres on a 105 × 68 pitch (IFAB), chalk on the turf. Drawn once, rotated for portrait. */
function Markings() {
  const s = 'fill-none stroke-white/75'
  return (
    <g strokeWidth={0.35} className={s}>
      <rect x={0.5} y={0.5} width={104} height={67} rx={0.5} />
      <line x1={52.5} y1={0.5} x2={52.5} y2={67.5} />
      <circle cx={52.5} cy={34} r={9.15} />
      <circle cx={52.5} cy={34} r={0.45} className="fill-white/75 stroke-none" />
      {[0, 1].map((side) => {
        const m = (x: number) => (side ? 105 - x : x)
        const dir = side ? -1 : 1
        return (
          <g key={side}>
            <rect x={Math.min(m(0.5), m(16.5))} y={13.84} width={16} height={40.32} />
            <rect x={Math.min(m(0.5), m(5.5))} y={24.84} width={5} height={18.32} />
            <circle cx={m(11)} cy={34} r={0.45} className="fill-white/75 stroke-none" />
            {/* Penalty arc: the part of the 9.15 m circle outside the box. */}
            <path d={`M${m(16.5)},${34 - 7.31}A9.15,9.15 0 0 ${side ? 0 : 1} ${m(16.5)},${34 + 7.31}`} />
            <path d={`M${m(0.5)},${1.5}A1,1 0 0 ${side ? 1 : 0} ${m(0.5) + dir},${0.5}`} />
            <path d={`M${m(0.5)},${66.5}A1,1 0 0 ${side ? 0 : 1} ${m(0.5) + dir},${67.5}`} />
          </g>
        )
      })}
    </g>
  )
}

export function Pitch({ orientation = 'horizontal', label, children, className, half, ref }: PitchProps) {
  const vertical = orientation === 'vertical'
  return (
    <figure
      ref={ref}
      className={cx(
        // Rendered turf (~/art) under the lines. On grass the club colour disappears: inside the pitch the
        // accent is the broadcast yellow-green of the landing page, with dark text on it.
        'relative isolate w-full overflow-hidden rounded-card bg-[#4b8a3d] ring-1 ring-black/10 ring-inset [--accent-ink:#111114] [--accent:#d6f35f]',
        vertical ? (half ? 'aspect-[136/105]' : 'aspect-[68/105]') : 'aspect-[105/68]',
        className,
      )}
    >
      <span aria-hidden="true" className="absolute inset-0 -z-10">
        <Picture art={vertical ? PITCH_V : PITCH_H} sizes={vertical ? '26rem' : '(min-width: 768px) 700px, 100vw'} className="size-full object-cover" />
      </span>
      {/* The half pitch is the top of the portrait drawing, cut at the halfway line. */}
      <svg viewBox={vertical ? `0 0 68 ${half ? 52.5 : 105}` : '0 0 105 68'} className="absolute inset-0 size-full" aria-hidden="true">
        <g transform={vertical ? 'translate(68 0) rotate(90)' : undefined}>
          <Markings />
        </g>
      </svg>
      <figcaption className="sr-only">{label}</figcaption>
      <div className="absolute inset-0">{children}</div>
    </figure>
  )
}

type SpotProps = {
  /** 0..100, left → right of the rendered pitch. */
  x: number
  /** 0..100, top → bottom of the rendered pitch. */
  y: number
  children: ReactNode
}

/** Anything centred on a pitch coordinate (arrows, ball, custom markers). */
export function PitchSpot({ x, y, children }: SpotProps) {
  return (
    <div
      className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
      // Position is data (formation/tactics coordinates): dynamic geometry is the allowed inline style.
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      {children}
    </div>
  )
}

type PitchPlayerProps = {
  x: number
  y: number
  /** Shirt number or position code inside the disc. */
  number: ReactNode
  name: string
  /** Small status dot (e.g. warning for "angeschlagen"). */
  status?: Tone
  selected?: boolean
  /** Makes the disc a button (tactics editing). */
  onClick?: () => void
}

export function PitchPlayer({ x, y, number, name, status, selected, onClick }: PitchPlayerProps) {
  const disc = (
    <span
      className={cx(
        'relative grid size-9 place-items-center rounded-full text-small font-semibold tabular shadow-[0_1px_2px_rgb(0_0_0/0.12)] transition-colors md:size-10',
        selected ? 'bg-accent text-accent-ink' : 'bg-ink text-ink-inverse',
      )}
    >
      {number}
      {status && (
        <span className={cx('absolute -top-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-bg-subtle', toneFill[status])} />
      )}
    </span>
  )
  const nameTag = (
    <span className="mt-1 max-w-[5.5rem] truncate rounded-[4px] bg-surface/85 px-1.5 text-label font-medium tracking-normal text-ink">
      {name}
    </span>
  )
  return (
    <PitchSpot x={x} y={y}>
      {onClick ? (
        <button type="button" onClick={onClick} aria-pressed={selected} className="flex flex-col items-center rounded-control">
          {disc}
          {nameTag}
        </button>
      ) : (
        <>
          {disc}
          {nameTag}
        </>
      )}
    </PitchSpot>
  )
}
